import { api } from "./config";

// Customer Group API functions
export async function getAllCustomerGroups() {
  try {
    const response = await api().get("/customer-groups");
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getAllCustomerGroups error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch customer groups"
    };
  }
}

export async function getCustomerGroup(id) {
  try {
    const response = await api().get(`/customer-groups/${id}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getCustomerGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch customer group"
    };
  }
}

export async function createCustomerGroup(groupData) {
  try {
    const response = await api().post("/customer-groups", groupData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("createCustomerGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to create customer group"
    };
  }
}

export async function updateCustomerGroup(id, groupData) {
  try {
    const response = await api().put(`/customer-groups/${id}`, groupData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("updateCustomerGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to update customer group"
    };
  }
}

export async function deleteCustomerGroup(id) {
  try {
    const response = await api().delete(`/customer-groups/${id}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("deleteCustomerGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to delete customer group"
    };
  }
}

export async function bulkUpdateCustomerGroups(groupIds, action, data = {}) {
  try {
    const response = await api().post("/customer-groups/bulk-update", {
      groupIds,
      action,
      data
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("bulkUpdateCustomerGroups error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to perform bulk update"
    };
  }
}

// WhatsApp Multi-Instance API functions
export async function getAllWhatsAppStatus() {
  try {
    const response = await api().get("/whatsapp/groups/status");
    
    // The backend returns whatsappStatus as a flat string value
    // No need to extract from nested structure as the backend handles it
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getAllWhatsAppStatus error:", error);
    
    // If 401 and it's after socket authentication, wait and retry once
    if (error.response?.status === 401) {
      console.warn("🔄 Auth issue (401) - waiting 2 seconds and retrying once...");
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        const retryResponse = await api().get("/whatsapp/groups/status");
        console.log("✅ Status fetch succeeded on retry");
        return {
          success: true,
          data: retryResponse.data
        };
      } catch (retryError) {
        console.error("❌ Status fetch still failed after retry:", retryError);
        return {
          success: false,
          message: retryError.response?.data?.message || "Failed to fetch WhatsApp status (retry failed)"
        };
      }
    }
    
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch WhatsApp status"
    };
  }
}

export async function getWhatsAppGroupStatus(groupId) {
  try {
    const response = await api().get(`/whatsapp/groups/${groupId}/status`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getWhatsAppGroupStatus error:", error);
    
    // If 401, wait and retry once
    if (error.response?.status === 401) {
      console.warn("🔄 Auth issue (401) - waiting 2 seconds and retrying once...");
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      try {
        const retryResponse = await api().get(`/whatsapp/groups/${groupId}/status`);
        console.log("✅ Group status fetch succeeded on retry");
        return {
          success: true,
          data: retryResponse.data
        };
      } catch (retryError) {
        console.error("❌ Group status fetch still failed after retry:", retryError);
        return {
          success: false,
          message: retryError.response?.data?.message || "Failed to fetch group status (retry failed)"
        };
      }
    }
    
    return {
      success: false,
      message: error.response?.data?.message || "Failed to fetch WhatsApp group status"
    };
  }
}

export async function initializeWhatsAppGroup(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/initialize`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("initializeWhatsAppGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to initialize WhatsApp group"
    };
  }
}

export async function getWhatsAppGroupQR(groupId) {
  try {
    const response = await api().get(`/whatsapp/groups/${groupId}/qr`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getWhatsAppGroupQR error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "QR code not available"
    };
  }
}

export async function sendWhatsAppGroupMessage(groupId, phoneNumber, message) {
  try {
    const response = await api().post("/whatsapp/jobs", {
      messageType: "manual_message",
      source: {
        scene: "whatsappgroup",
        action: "manual_message",
      },
      customerGroupId: groupId,
      destination: {
        phoneNumber,
      },
      content: {
        text: message,
      },
      provider: {
        selectedGroupId: groupId,
        resolvedBy: "manual_selection",
      },
    });

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("sendWhatsAppGroupMessage error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to queue message"
    };
  }
}

export async function restartWhatsAppGroup(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/restart`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("restartWhatsAppGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to restart WhatsApp group"
    };
  }
}

export async function destroyWhatsAppGroup(groupId) {
  try {
    const response = await api().delete(`/whatsapp/groups/${groupId}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("destroyWhatsAppGroup error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to destroy WhatsApp group"
    };
  }
}

export async function sendCustomerMessage(customerId, message) {
  try {
    const response = await api().post("/whatsapp/customers/send", {
      customerId,
      message
    });
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("sendCustomerMessage error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to send customer message"
    };
  }
}

// Default Instance Management
export async function setDefaultInstance(groupId) {
  try {
    console.log("API: Setting default instance for group:", groupId);
    const response = await api().post(`/customer-groups/${groupId}/set-default`);
    console.log("API: Set default response:", response.data);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("setDefaultInstance API error:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error status:", error.response?.status);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to set default instance"
    };
  }
}

export async function getDefaultInstance() {
  try {
    const response = await api().get("/customer-groups/default-instance/current");
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("getDefaultInstance error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to get default instance"
    };
  }
}

// Session Management Functions
export async function clearSessionData(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/clear-session`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("clearSessionData error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to clear session data"
    };
  }
}

export async function completeReinitialization(groupId) {
  try {
    // Complete reset can take 15-20 seconds, use extended timeout
    const response = await api().post(
      `/whatsapp/groups/${groupId}/complete-reinit`,
      {},
      { timeout: 60000 } // 60 second timeout for complete reset
    );
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("completeReinitialization error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to complete reinitialization"
    };
  }
}

export async function recoverAuthentication(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/recover-auth`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("recoverAuthentication error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to recover authentication"
    };
  }
}

export async function forceReinitialize(groupId) {
  try {
    const response = await api().post(`/whatsapp/groups/${groupId}/reinitialize`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error("forceReinitialize error:", error);
    return {
      success: false,
      message: error.response?.data?.message || "Failed to force reinitialize"
    };
  }
}